import React, { useState, useEffect } from "react";
import BookRecommenderForm from "./BookRecommenderForm.js";
import RecommendedBookCard from "./RecommendedBookCard.js";
import Modal from "@mui/material/Modal";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { useMediaQuery } from "@mui/material";

const SNACKBAR_DURATION = 2000;

const BookRecommender = ({ setIsLoading }) => {
  const isMobile = useMediaQuery("(max-width: 600px)"); // Check if viewport is mobile
  const [hasError, setHasError] = useState(false);
  const [isDisplayingToast, setIsDisplayingToast] = useState(false);
  const [isRecommenderModalOpen, setIsRecommenderModalOpen] = useState(false);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [recommendedBook, setRecommendedBook] = useState(null);
  const [historyOfRecommendBooks, setHistoryOfRecommendedBooks] = useState([]);
  const [recommendedBookLoading, setRecommendedBookLoading] = useState(false);

  const handleCloseToast = (_event, reason) => {
    if (reason === "clickaway") return;
    setIsDisplayingToast(false);
  };

  useEffect(() => {
    setIsLoading(recommendedBookLoading);
  }, [recommendedBookLoading, setIsLoading]);

  const getRecommendedBook = async () => {
    if (recommendedBook && !isRecommenderModalOpen) {
      setIsRecommenderModalOpen(true);
      return;
    }

    setHasError(false);

    try {
      setRecommendedBookLoading(true);

      const response = await fetch("/api/recommendation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          books: selectedBooks
            .filter((sb) => sb)
            .map((sb) => `${sb.title} by ${sb.author}`),
          alreadyRecommendedBooks: historyOfRecommendBooks.length
            ? historyOfRecommendBooks.map((rb) => `${rb.title} by ${rb.author}`)
            : null,
        }),
      });

      const newRecommendedBook = await response.json();

      // Set the recommended selectedBooks data from the response
      setRecommendedBook(newRecommendedBook);
      setHistoryOfRecommendedBooks([
        ...historyOfRecommendBooks,
        newRecommendedBook,
      ]);
      setIsRecommenderModalOpen(true);
    } catch {
      setHasError(true);
    } finally {
      setIsDisplayingToast(true);
      setRecommendedBookLoading(false);
    }
  };

  return (
    <div
      style={{
        ...pageContainerStyle,
        ...(isMobile ? mobilePageContainerStyle : {}),
        maxHeight: "90vh", // Limit vertical expansion
      }}
    >
      {!recommendedBook || !isRecommenderModalOpen ? (
        <BookRecommenderForm
          recommendedBookLoading={recommendedBookLoading}
          getRecommendedBook={getRecommendedBook}
          recommendedBook={recommendedBook}
          selectedBooks={selectedBooks}
          setSelectedBooks={setSelectedBooks}
          setRecommendedBook={setRecommendedBook}
          isMobile={isMobile} // Pass down mobile state
        />
      ) : (
        <Modal
          open={isRecommenderModalOpen}
          onClose={() =>
            !recommendedBookLoading && setIsRecommenderModalOpen(false)
          }
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          <div
            style={{
              ...modalContentStyle,
              ...(isMobile ? mobileModalContentStyle : {}),
            }}
          >
            <RecommendedBookCard
              setIsRecommenderModalOpen={setIsRecommenderModalOpen}
              historyOfRecommendBooks={historyOfRecommendBooks}
              recommendedBookLoading={recommendedBookLoading}
              getRecommendedBook={getRecommendedBook}
              recommendedBook={recommendedBook}
              setRecommendedBook={setRecommendedBook}
              selectedBooks={selectedBooks}
              isMobile={isMobile} // Pass down mobile state
            />
          </div>
        </Modal>
      )}

      {recommendedBookLoading && <CircularProgress style={spinnerStyle} />}

      <Snackbar
        open={isDisplayingToast}
        autoHideDuration={SNACKBAR_DURATION}
        onClose={handleCloseToast}
      >
        <Alert
          onClose={handleCloseToast}
          severity={hasError ? "error" : "success"}
          variant="filled"
          sx={{ width: "100%", fontFamily: "Manrope, sans-serif" }}
        >
          {hasError
            ? "Could not generate a recommendation. Please try again."
            : "Recommendation generated successfully."}
        </Alert>
      </Snackbar>
    </div>
  );
};

const spinnerStyle = {
  zIndex: "1",
  position: "absolute",
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
};

const pageContainerStyle = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "start",
  alignItems: "center",
  backgroundColor: "#F4F5FB",
  padding: "20px",
  flexGrow: 1,
  boxShadow: "inset 0 0 15px rgba(0, 0, 0, 0.05)",
  borderRadius: "12px",
};

const mobilePageContainerStyle = {
  padding: "10px", // Reduce padding for mobile
};

const modalContentStyle = {
  position: "absolute",
  top: "50%",
  height: "700px",
  overflow: "auto",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "white",
  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
  padding: "20px",
  paddingTop: "10px",
  borderRadius: "8px",
  outline: "none",
  width: "100%",
  maxWidth: "500px",
};

const mobileModalContentStyle = {
  width: "90%", // Expand modal width for mobile
  maxWidth: "none", // Allow flexible resizing
};

export default BookRecommender;
